<template>
	<div>
		<ion-button size="small" color="medium" @click="confirmGetResetLink">
			{{ $t("user-management.get-password-reset-link") }}
		</ion-button>

		<!-- Result Modal -->
		<sg-modal
			v-model="showResultModal"
			:title="$t('user-management.password-reset-link-title')"
			header-color="branding"
		>
			<div class="flex flex-col gap-4 p-4">
				<p class="text-sm text-gray-700">
					{{ $t("user-management.password-reset-link-instructions") }}
				</p>

				<!-- Reset Link Display -->
				<div
					class="m-2 break-all rounded-md bg-gray-100 px-2 py-3 text-center text-sm"
				>
					{{ resetLink }}
				</div>

				<!-- Action Buttons -->
				<div class="flex justify-center gap-2">
					<ion-button
						v-if="copyIsSupported"
						size="small"
						fill="outline"
						@click="handleCopy"
					>
						<ion-icon slot="start" :icon="copyOutline" />
						{{ $t("common.copy") }}
					</ion-button>

					<ion-button
						v-if="shareIsSupported"
						size="small"
						fill="outline"
						color="success"
						@click="handleShare"
					>
						<ion-icon slot="start" :icon="shareOutline" />
						{{ $t("common.share") }}
					</ion-button>
				</div>

				<!-- QR Code -->
				<div class="flex justify-center py-4">
					<QRCode :url="resetLink" />
				</div>
			</div>
		</sg-modal>
	</div>
</template>

<script setup lang="ts">
	import { ref } from "vue";
	import {
		alertController,
		loadingController,
		IonButton,
		IonIcon,
	} from "@ionic/vue";
	import { copyOutline, shareOutline } from "ionicons/icons";
	import { useI18n } from "vue-i18n";
	import { useClipboard, useShare } from "@vueuse/core";
	import { GVAUserPrivilegedDTO } from "seedgreen-shared/dtos/GVAUserDTO";
	import { useGetResetLink } from "seedgreen-shared/queries/forgot-password";
	import { sendToast } from "@/utils/sendToast";
	import QRCode from "@/components/ui/QRCode.vue";
	import SgModal from "@/components/ui/modals/SgModal.vue";

	const props = defineProps<{
		user: GVAUserPrivilegedDTO;
	}>();

	const { t } = useI18n();
	const { copy, isSupported: copyIsSupported } = useClipboard();
	const { share, isSupported: shareIsSupported } = useShare();
	const { getResetLink: getResetLinkMutation } = useGetResetLink();

	const showResultModal = ref(false);
	const resetLink = ref("");

	/**
	 * Show confirmation alert before generating reset link
	 */
	async function confirmGetResetLink() {
		const alert = await alertController.create({
			header: t("user-management.password-reset-managed-header"),
			message: t("user-management.password-reset-managed-message"),
			buttons: [
				{
					text: t("common.cancel"),
					role: "cancel",
				},
				{
					text: t("common.continue"),
					handler: async () => {
						await getResetLink();
					},
				},
			],
		});
		await alert.present();
	}

	/**
	 * Call the API to get the password reset link
	 */
	async function getResetLink() {
		const loading = await loadingController.create({
			message: t("user-management.getting-reset-link"),
		});
		await loading.present();

		try {
			const link = await getResetLinkMutation(props.user.id);

			// Check if the response is null (rate limited)
			if (link === null) {
				await loading.dismiss();
				await showRateLimitAlert();
				return;
			}

			resetLink.value = link;
			showResultModal.value = true;
		} catch (error) {
			console.error("Failed to get reset link:", error);
			await showErrorAlert();
		} finally {
			await loading.dismiss();
		}
	}

	/**
	 * Show error alert when API call fails
	 */
	async function showErrorAlert() {
		const alert = await alertController.create({
			header: t("error.error"),
			message: t("user-management.password-reset-link-error"),
			buttons: [t("common.ok")],
		});
		await alert.present();
	}

	/**
	 * Show alert when user is rate limited
	 */
	async function showRateLimitAlert() {
		const alert = await alertController.create({
			header: t("error.error"),
			message: t("user-management.password-reset-link-rate-limited"),
			buttons: [t("common.ok")],
		});
		await alert.present();
	}

	/**
	 * Copy the reset link to clipboard
	 */
	async function handleCopy() {
		const message = `Password Reset for ${props.user.name ?? props.user.userName}

Username: ${props.user.userName}
Reset your password here: ${resetLink.value}`;

		try {
			await copy(message);
			await sendToast(
				t("user-management.password-reset-link-copied"),
				2000,
				"success",
			);
		} catch (error) {
			console.error("Failed to copy link:", error);
			await sendToast(t("invite-modal.failed-to-copy"), 2000, "danger");
		}
	}

	/**
	 * Share the reset link using the Web Share API
	 */
	async function handleShare() {
		try {
			await share({
				title: "SeedGreen Password Reset",
				text: `Password reset link for ${props.user.userName}`,
				url: resetLink.value,
			});
		} catch (error) {
			// User canceling the share dialog is normal behavior, don't show error
			if (error instanceof Error && error.name === "AbortError") {
				return;
			}

			console.error("Failed to share link:", error);
			await sendToast(t("invite-modal.failed-to-share"), 2000, "danger");
		}
	}
</script>
